import React, { useState, useEffect } from 'react';
import { useTheme } from '../ThemeContext';
import { EarningsService, EarningsCalendarItem } from '../../services/EarningsService';
import { EarningsCard } from './EarningsCard';
import { ChevronRight, Calendar as CalendarIcon, Loader, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const EarningsInsightsPreview: React.FC = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [earnings, setEarnings] = useState<EarningsCalendarItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDate] = useState(new Date());

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const getWeekRange = (date: Date) => {
        const start = new Date(date);
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        start.setHours(0, 0, 0, 0);

        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);

        return { start, end };
    };

    const fetchEarnings = async () => {
        setLoading(true);
        const { start, end } = getWeekRange(currentDate);

        try {
            const data = await EarningsService.getEarningsCalendar(formatDate(start), formatDate(end));
            // Filter for today and future days in the week, or just show all for the week
            // Let's show today's earnings primarily, or a mix
            setEarnings(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEarnings();
    }, [currentDate]);

    // Group by date
    const groupedEarnings = earnings.reduce((acc, item) => {
        if (!acc[item.date]) acc[item.date] = [];
        acc[item.date].push(item);
        return acc;
    }, {} as Record<string, EarningsCalendarItem[]>);

    // Get today's earnings or the next available day
    const todayStr = formatDate(new Date());
    const todayEarnings = groupedEarnings[todayStr] || [];

    // If no earnings today, find the next day with earnings
    let displayDate = todayStr;
    let displayEarnings = todayEarnings;

    if (displayEarnings.length === 0) {
        const sortedDates = Object.keys(groupedEarnings).sort();
        const nextDate = sortedDates.find(d => d > todayStr);
        if (nextDate) {
            displayDate = nextDate;
            displayEarnings = groupedEarnings[nextDate];
        }
    }

    // Limit to 4 items for preview
    const previewItems = displayEarnings.slice(0, 4);

    return (
        <div className={`rounded-xl border p-6 ${isDark ? 'bg-theme-secondary border-theme-tertiary' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                        <CalendarIcon size={24} />
                    </div>
                    <div>
                        <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Earnings Insights
                        </h2>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {displayDate === todayStr ? "Today's Highlights" : `Upcoming: ${new Date(displayDate).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}`}
                        </p>
                    </div>
                </div>
                <Link
                    to="/earnings"
                    className={`flex items-center gap-1 text-sm font-medium transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                        }`}
                >
                    View Calendar <ArrowRight size={16} />
                </Link>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <Loader className="animate-spin text-blue-500" size={32} />
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {previewItems.length > 0 ? (
                        previewItems.map((item, idx) => (
                            <EarningsCard key={`${item.symbol}-${idx}`} item={item} viewType="advanced" />
                        ))
                    ) : (
                        <div className={`col-span-full text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            No earnings scheduled for this week.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
